/*
  The numbers shown after a tab's name. Only the Review tab has one: how many things wait for the person to look at. These are
  the ontology's own counts (the elements waiting for the expert, what came back to the engineer). The bar (frame/ViewTabs) only
  draws the numbers it is given by tab id; this file and screens.ts are where the ontology says which tabs it has and what they count,
  and a second product on the same frame says its own there (DEC-1104-06).
*/

import { useQuery } from "@tanstack/react-query";

import { runQuery } from "../api/endpoints";
import type { Counters, MeView, Role } from "../api/types";
import { useLive } from "../live/store";
import { activeRoles } from "./access";

/** What waits for a person: an expert has the elements waiting for them; an engineer has what was sent back and the notes to answer. */
export function reviewCount(roles: readonly Role[], counters: Counters): number {
  let count = 0;
  if (roles.includes("sme")) count += counters.waiting;
  if (roles.includes("engineer")) count += counters.sent_back + counters.notes_waiting;
  return count;
}

/**
 * The badges of the bar, by tab id. The count follows the live channel: the room's counts, which the server pushes after every
 * change, and until the first push a read of the counters. The read has a key of its own, so what a screen read under the plain
 * key is left alone, and it is made only while no push has come.
 */
export function useViewBadges(me: MeView): Record<string, number> {
  const live = useLive();
  const roles = activeRoles(me);
  const eligible = !!me.active && (roles.includes("engineer") || roles.includes("sme"));
  const read = useQuery({
    queryKey: ["counters", { place: "bar" }],
    queryFn: () => runQuery("counters", {}),
    enabled: eligible && live.counts === null,
    staleTime: 15_000,
    retry: false,
  });
  const counters = live.counts?.counters ?? read.data;
  if (!eligible || !counters) return {};
  return { review: reviewCount(roles, counters) };
}
